/**
 * PDFPreviewOverlay - In-app PDF preview using Mozilla's pdf.js via react-pdf.
 *
 * Renders PDFs using the react-pdf library, which wraps pdfjs-dist.
 * Supports multiple items with arrow navigation in the header.
 *
 * The PDF is loaded from a Uint8Array (via IPC) and rendered to canvas.
 * The pdf.js worker handles decoding and rendering in a background thread.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { FileText } from 'lucide-react'
import { PreviewOverlay } from './PreviewOverlay'
import { CopyButton } from './CopyButton'
import { ItemNavigator } from './ItemNavigator'
import { ZoomControls } from './ZoomControls'
import { RICH_BLOCK_DEFAULTS } from './rich-block-interaction-spec'
import { clampScale, computeFitScale, zoomStepScale } from './useRichBlockInteractions'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Configure pdf.js worker using Vite's ?url import for cross-platform dev/prod compatibility
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker

interface PreviewItem {
  src: string
  label?: string
}

export interface PDFPreviewOverlayProps {
  isOpen: boolean
  onClose: () => void
  /** Absolute file path for the PDF (single item / backward compat) */
  filePath: string
  /** Multiple items for arrow navigation */
  items?: PreviewItem[]
  /** Initial active item index (defaults to 0) */
  initialIndex?: number
  /** Async loader that returns PDF data as Uint8Array */
  loadPdfData: (path: string) => Promise<Uint8Array>
  theme?: 'light' | 'dark'
}

interface LoadedPdfPage {
  getViewport: (options: { scale: number }) => { width: number; height: number }
}

interface LoadedPdfDocument {
  numPages: number
  getPage: (pageNumber: number) => Promise<LoadedPdfPage>
}

export function PDFPreviewOverlay({
  isOpen,
  onClose,
  filePath,
  items,
  initialIndex = 0,
  loadPdfData,
  theme = 'light',
}: PDFPreviewOverlayProps) {
  // Normalize: items array or single filePath
  const resolvedItems = useMemo<PreviewItem[]>(() => {
    if (items && items.length > 0) return items
    return [{ src: filePath }]
  }, [items, filePath])

  const [activeIdx, setActiveIdx] = useState(initialIndex)
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [scale, setScale] = useState(1)
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const documentRef = useRef<HTMLDivElement>(null)
  const pageSizeRequestIdRef = useRef(0)

  const activeItem = resolvedItems[activeIdx]

  // Reset index when overlay opens
  useEffect(() => {
    if (isOpen) {
      setActiveIdx(initialIndex)
      setScale(1)
      setPageSize(null)
    }
  }, [isOpen, initialIndex])

  useEffect(() => {
    pageSizeRequestIdRef.current += 1
    if (!isOpen) return
    setScale(1)
    setPageSize(null)
    requestAnimationFrame(() => {
      containerRef.current?.scrollTo({ top: 0, left: 0 })
    })
  }, [isOpen, activeItem?.src])

  // Load PDF data when overlay opens or active item changes
  useEffect(() => {
    if (!isOpen || !activeItem?.src) return

    let cancelled = false
    setIsLoading(true)
    setError(null)
    setPdfData(null)
    setNumPages(0)

    loadPdfData(activeItem.src)
      .then((data) => {
        if (!cancelled) {
          setPdfData(data)
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF')
          setIsLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [isOpen, activeItem?.src, loadPdfData])

  const updateScale = useCallback((
    nextValue: number | ((prev: number) => number),
    anchor?: { clientX: number; clientY: number },
  ) => {
    const container = containerRef.current
    const containerRect = container?.getBoundingClientRect()
    const contentRect = documentRef.current?.getBoundingClientRect() ?? containerRect
    const fallbackAnchor = anchor ?? (
      containerRect
        ? {
            clientX: containerRect.left + containerRect.width / 2,
            clientY: containerRect.top + containerRect.height / 2,
          }
        : undefined
    )
    const contentOffsetX = container && containerRect && contentRect
      ? container.scrollLeft + (contentRect.left - containerRect.left)
      : 0
    const contentOffsetY = container && containerRect && contentRect
      ? container.scrollTop + (contentRect.top - containerRect.top)
      : 0
    const viewportX = contentRect && fallbackAnchor ? fallbackAnchor.clientX - contentRect.left : 0
    const viewportY = contentRect && fallbackAnchor ? fallbackAnchor.clientY - contentRect.top : 0
    const contentX = contentOffsetX + viewportX
    const contentY = contentOffsetY + viewportY

    setScale((prev) => {
      const requested = typeof nextValue === 'function' ? nextValue(prev) : nextValue
      const next = clampScale(requested, RICH_BLOCK_DEFAULTS.minScale, RICH_BLOCK_DEFAULTS.maxScale)

      if (container && next !== prev) {
        const ratio = next / prev
        requestAnimationFrame(() => {
          const current = containerRef.current
          if (!current) return
          const nextContentRect = documentRef.current?.getBoundingClientRect()
          const nextViewportX = nextContentRect && fallbackAnchor ? fallbackAnchor.clientX - nextContentRect.left : viewportX
          const nextViewportY = nextContentRect && fallbackAnchor ? fallbackAnchor.clientY - nextContentRect.top : viewportY
          current.scrollLeft = Math.max(0, contentX * ratio - nextViewportX)
          current.scrollTop = Math.max(0, contentY * ratio - nextViewportY)
        })
      }

      return next
    })
  }, [])

  const zoomByStep = useCallback((direction: 'in' | 'out') => {
    updateScale((prev) => zoomStepScale(
      prev,
      direction,
      RICH_BLOCK_DEFAULTS.zoomStepFactor,
      RICH_BLOCK_DEFAULTS.minScale,
      RICH_BLOCK_DEFAULTS.maxScale,
    ))
  }, [updateScale])

  const zoomToPreset = useCallback((percent: number) => {
    updateScale(percent / 100)
  }, [updateScale])

  const zoomToFit = useCallback(() => {
    const container = containerRef.current
    if (!container || !pageSize) {
      setScale(1)
      return
    }

    const zoomWrapper = documentRef.current?.parentElement
    const wrapperStyle = zoomWrapper ? window.getComputedStyle(zoomWrapper) : null
    const availableWidth = Math.max(
      0,
      container.clientWidth - (
        wrapperStyle
          ? Number.parseFloat(wrapperStyle.paddingLeft) + Number.parseFloat(wrapperStyle.paddingRight)
          : 0
      ),
    )
    const availableHeight = Math.max(
      0,
      container.clientHeight - (
        wrapperStyle
          ? Number.parseFloat(wrapperStyle.paddingTop) + Number.parseFloat(wrapperStyle.paddingBottom)
          : 0
      ),
    )
    const fitScale = computeFitScale(
      { width: availableWidth, height: availableHeight },
      pageSize,
      RICH_BLOCK_DEFAULTS.minScale,
      RICH_BLOCK_DEFAULTS.maxScale,
    )

    setScale(fitScale)
    requestAnimationFrame(() => {
      containerRef.current?.scrollTo({ top: 0, left: 0 })
    })
  }, [pageSize])

  const resetZoom = useCallback(() => {
    setScale(1)
    requestAnimationFrame(() => {
      containerRef.current?.scrollTo({ top: 0, left: 0 })
    })
  }, [])

  const onDocumentLoadSuccess = useCallback((pdf: LoadedPdfDocument) => {
    const requestId = pageSizeRequestIdRef.current
    setNumPages(pdf.numPages)
    void pdf.getPage(1)
      .then((page) => {
        if (requestId !== pageSizeRequestIdRef.current) return
        const viewport = page.getViewport({ scale: 1 })
        setPageSize({ width: viewport.width, height: viewport.height })
      })
      .catch(() => {
        if (requestId !== pageSizeRequestIdRef.current) return
        setPageSize(null)
      })
  }, [])

  const onDocumentLoadError = useCallback((error: Error) => {
    setError(`Failed to load PDF: ${error.message}`)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!isOpen || !container) return

    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return

      e.preventDefault()
      e.stopPropagation()

      const factor = Math.pow(2, -e.deltaY * RICH_BLOCK_DEFAULTS.wheelSensitivity.trackpadPinch)
      updateScale((prev) => prev * factor, { clientX: e.clientX, clientY: e.clientY })
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [isOpen, updateScale])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return

      if (e.key === '=' || e.key === '+') {
        e.preventDefault()
        zoomByStep('in')
      } else if (e.key === '-') {
        e.preventDefault()
        zoomByStep('out')
      } else if (e.key === '0') {
        e.preventDefault()
        resetZoom()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, resetZoom, zoomByStep])

  // Memoize file object to prevent unnecessary re-renders (react-pdf uses === equality)
  const fileObj = useMemo(() =>
    pdfData ? { data: pdfData } : null,
    [pdfData]
  )

  // Header actions: item navigation + copy button
  const isDefaultZoom = scale === 1
  const headerActions = (
    <div className="flex items-center gap-2">
      <ItemNavigator items={resolvedItems} activeIndex={activeIdx} onSelect={setActiveIdx} size="md" />
      <ZoomControls
        scale={scale}
        minScale={RICH_BLOCK_DEFAULTS.minScale}
        maxScale={RICH_BLOCK_DEFAULTS.maxScale}
        zoomPresets={RICH_BLOCK_DEFAULTS.zoomPresets}
        onZoomIn={() => zoomByStep('in')}
        onZoomOut={() => zoomByStep('out')}
        onZoomToPreset={zoomToPreset}
        onZoomToFit={zoomToFit}
        onReset={resetZoom}
        resetDisabled={isDefaultZoom}
      />
      <CopyButton content={activeItem?.src || filePath} title="Copy path" className="bg-background shadow-minimal" />
    </div>
  )

  return (
    <PreviewOverlay
      isOpen={isOpen}
      onClose={onClose}
      theme={theme}
      typeBadge={{
        icon: FileText,
        label: 'PDF',
        variant: 'orange',
      }}
      filePath={activeItem?.src || filePath}
      error={error ? { label: 'Load Failed', message: error } : undefined}
      headerActions={headerActions}
    >
      <div ref={containerRef} className="h-full overflow-auto overscroll-contain">
        {isLoading && (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Loading PDF...</div>
        )}
        {fileObj && (
          <div className="min-h-full w-fit min-w-full flex justify-center p-4">
            <div ref={documentRef} className="w-fit">
              <Document
                file={fileObj}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="text-muted-foreground text-sm">Rendering...</div>}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="pdf-page pdf-page--zoomable"
                  />
                ))}
              </Document>
            </div>
          </div>
        )}
      </div>
    </PreviewOverlay>
  )
}
